import { useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { Container, Stack } from '@mui/material';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import { ErrorCode } from '@watershed/errors/ErrorRegistry';
import SyncIcon from '@watershed/icons/components/Sync';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import useEffectOnce from '@watershed/shared-frontend/hooks/useEffectOnce';
import { maybeNotifySentry } from '@watershed/shared-frontend/utils/errorUtils';
import frontendHoneycomb from '@watershed/shared-frontend/utils/frontendHoneycomb';
import { CustomSpanAttributes } from '@watershed/shared-universal/constants/otelCustomAttributes';
import { routeForSupport } from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import {
  Error,
  FlexiError,
} from '@watershed/ui-core/components/shared/ErrorDisplayComponents';
import TextLink from '@watershed/ui-core/components/TextLink';
import { UnexpectedErrorState } from '@watershed/ui-core/components/UnexpectedErrorState';

import { ForbiddenPageBody } from '../components/ForbiddenPage';
import { NotFoundPageBody } from '../components/NotFoundPage';
import { useUserContext } from '../utils/UserContext';

function CrossOrgErrorPageBody({ error }: { error: FlexiError }) {
  const user = useUserContext();

  useEffect(() => {
    if (
      !user.loginAsUser &&
      !user.userIsWatershedContractor &&
      !user.userIsWatershedEmployee
    )
      maybeNotifySentry(error);
  }, [error, user]);

  return (
    <Error.Root>
      <Error.Icon error={error} />
      <Error.Title>
        <Trans context="status">
          Unable to load across organizations while authenticated
        </Trans>
      </Error.Title>
      <Error.Description>
        <Trans context="referring to a browser tab that has been open too long">
          This is likely due to data being stale in your browser. Please try
          refreshing the page.
        </Trans>
      </Error.Description>
    </Error.Root>
  );
}

function LogoutErrorPageBody({ error }: { error: FlexiError }) {
  return (
    <Error.Root>
      <Error.Icon error={error} testId={TestIds.ErrorWarningIcon} />
      <Error.Title>
        <Trans>Login session has expired</Trans>
      </Error.Title>
      <Error.Description>
        <Trans context="status">
          You were logged out due to inactivity. We'll now redirect you to the
          login page.
        </Trans>
      </Error.Description>
    </Error.Root>
  );
}

function GraphqlSchemaErrorPageBody({ error }: { error: FlexiError }) {
  useEffect(() => {
    maybeNotifySentry(error);
  }, [error]);

  return (
    <Error.Root>
      <Error.Icon error={error} testId={TestIds.ErrorSyncIcon} />
      <Error.Title>
        <Trans>Application is out of sync with your browser</Trans>
      </Error.Title>
      <Error.Description>
        <Trans>Please reload the page to fix the issue.</Trans>
      </Error.Description>
      <Button
        startIcon={<SyncIcon />}
        onClick={() => window.location.reload()}
        sx={{ marginTop: 1 }}
      >
        <Trans context="button copy">Reload this page</Trans>
      </Button>
    </Error.Root>
  );
}

function DataResponseTooLargeErrorBody({ error }: { error: FlexiError }) {
  return (
    <Error.Root>
      <Error.Icon error={error} testId={TestIds.ErrorWarningIcon} />
      <Error.Title role="alert">
        <Trans context="oh no too big!">
          The size of the data requested is too large
        </Trans>
      </Error.Title>
      <Error.Description>
        <Trans context="support link">
          Try narrowing your query or contact support for assistance via the{' '}
          <TextLink
            data-testid={TestIds.ErrorSupportLink}
            href={routeForSupport()}
          >
            Support center
          </TextLink>
          .
        </Trans>
      </Error.Description>
    </Error.Root>
  );
}

/**
 * Internationalized strings must be rendered within an I18nProvider. However,
 * there are certain cases under which we cannot render these strings, e.g.
 * - Errors thrown outside of the I18nProvider
 * - Errors loading the translations for internationalized strings, i.e. MessageLoadingError
 * For these cases, we'll render an uninternationalized error page.
 */
function UninternationalizedErrorBody({ error }: { error: FlexiError }) {
  useEffect(() => {
    maybeNotifySentry(error);
  }, [error]);

  return (
    <Error.Root>
      <Error.Icon error={error} />
      <Error.Title>
        <Trans>Something went wrong</Trans>
      </Error.Title>
      <Error.Description data-testid={TestIds.ErrorPageBody}>
        <Trans>
          Our engineering team has been notified and will investigate the issue.
          Reloading the page or hitting the back button in your browser may
          resolve the issue. If you have additional questions, please contact
          support via the{' '}
        </Trans>
        <TextLink
          data-testid={TestIds.ErrorSupportLink}
          href={routeForSupport()}
        >
          <Trans>Support center</Trans>
        </TextLink>
        .
      </Error.Description>
    </Error.Root>
  );
}

/**
 * Map of error codes to custom error pages. If an error does not have a code
 * with a custom page, the fallback "Unexpected error" is rendered.
 */
const errorPageBodyForCode: Partial<
  Record<ErrorCode, (...args: Array<any>) => JSX.Element>
> = {
  CROSS_ORG: CrossOrgErrorPageBody,
  UNAUTHENTICATED: LogoutErrorPageBody,
  FORBIDDEN: ForbiddenPageBody,
  NOT_FOUND: NotFoundPageBody,
  GRAPHQL_VALIDATION_FAILED: GraphqlSchemaErrorPageBody,
  DATA_RESPONSE_TOO_LARGE: DataResponseTooLargeErrorBody,
  MESSAGE_LOADING_ERROR: UninternationalizedErrorBody,
};

function getErrorPageForCode(error: FlexiError): JSX.Element {
  const ErrorPageBody =
    (error.code ? errorPageBodyForCode[error.code as ErrorCode] : null) ??
    UnexpectedErrorState;
  return <ErrorPageBody error={error} />;
}

async function sendErrorEventToHoneycomb(
  error?: FlexiError,
  details?: Record<string, unknown>
) {
  await frontendHoneycomb.sendEvent({
    name: CustomSpanAttributes.BROWSER_USER_VISIBLE_ERROR_PAGE,
    [CustomSpanAttributes.BROWSER_USER_VISIBLE_ERROR_CODE]:
      error?.code ?? 'GENERIC',
    ...(details ? { details } : {}),
  });
}

export default function ErrorPage({
  error,
  details,
}: {
  error: FlexiError;
  details?: Record<string, unknown>;
}) {
  useEffect(() => {
    console.error(error);
    Analytics.error('errorPageShown', { code: error.code });
    void sendErrorEventToHoneycomb(error, details);
  }, [error, details]);

  return (
    <PageContainer
      isFullPage={false}
      maxWidth="sm"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        paddingTop: 64,
        paddingBottom: 64,
      }}
      testId={TestIds.ErrorStateFullPage}
    >
      {getErrorPageForCode(error)}
    </PageContainer>
  );
}

export function GenericUnexpectedErrorPage() {
  useEffectOnce(() => {
    void sendErrorEventToHoneycomb();
  });
  return (
    <Container
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBlock: 64,
        paddingInline: 32,
        width: '100vw',
        height: '100vh',
      }}
    >
      <Stack
        maxWidth="sm"
        direction="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <UnexpectedErrorState />
      </Stack>
    </Container>
  );
}
